import {
  normalizePattern,
  type ExclusionRule,
} from './sync-exclusion-matcher.js'
import { isValidFolderRetention } from './version-retention-bounds.js'

/**
 * The seeded folder rules — directories whose version history is capped low by
 * default (#616).
 *
 * Until this existed, `skipExtensions` was the only content filter the sync
 * had, it held exactly `.fdb` and `.gdb`, and it keyed on file EXTENSION. So a
 * `git clone` + `npm install` inside an agent workspace put roughly 120,000
 * tool-private files into `files`, `file_versions` and the CAS as ordinary user
 * content, and every later `git status` — which rewrites the 1.4 MB
 * `.git/index` — filed another binary version of a file the user does not know
 * exists.
 *
 * ── WHAT A LOW CAP FIXES, AND WHAT IT DOES NOT ────────────────────────────
 *
 * It fixes the CHURN half: `.git/index` at one version stops accumulating a
 * new multi-megabyte blob per `git status`, and a reinstalled `node_modules`
 * stops stacking history for files nobody will restore.
 *
 * It does NOT make the directory cheap. The walk still descends, stats, reads,
 * hashes, writes a `files` row and a CAS blob per file. That cost was the
 * other half of #616 and an earlier shape of this feature removed it with a
 * fourth rule state meaning "never descend" — see `FolderRetention` for the
 * three surfaces that then disagreed about whether those files existed, which
 * is why the state is gone. Reintroducing it means answering those first.
 *
 * ── THE ADMISSION RULE ────────────────────────────────────────────────────
 *
 * A name belongs on this list only if BOTH hold:
 *
 *   1. it is TOOL-PRIVATE — the directory is created and owned by a
 *      development tool, and a person would not choose that exact name for
 *      their own content; and
 *   2. its contents are REPRODUCIBLE from something else in the tree (a
 *      manifest, a remote, a source file), so keeping one version of it loses
 *      nothing a user could not regenerate.
 *
 * Both halves matter, and the FIRST is the one that keeps this safe. `dist`,
 * `build`, `out`, `target` and `venv` all satisfy (2) and fail (1): they are
 * ordinary English words, and a user's "build" folder of drawings silently
 * losing its history is a worse defect than the one this file fixes. Do not
 * add a name because it is *usually* a build artifact.
 *
 * ── THE LIST IS A SEED, NOT A FLOOR AND NOT A MERGE ───────────────────────
 *
 * It was a floor first: the resolver unioned these names in unconditionally,
 * so no tenant configuration could drop `.git`. Then it was a merge, layering
 * defaults under a tenant's own rules. Both were wrong for the same reason —
 * they made these three names a different KIND of thing from every other rule
 * on the screen, so the row for `node_modules` could be edited but never
 * renamed, never removed, and never fully explained.
 *
 * They are now a SEED. A tenant who has configured nothing resolves to exactly
 * this list; a tenant who has saved anything resolves to exactly what they
 * saved. `seedFolderRules` is the whole of the relationship, and once a tenant
 * has a stored list these constants are no longer consulted for them at all.
 *
 * The UI always round-trips the FULL list (the GET returns the seed for an
 * unconfigured tenant), so "saved anything" never means "saved a list that
 * accidentally omits the defaults".
 */

/**
 * How many versions a seeded directory keeps.
 *
 * One, not zero: a file that exists IS version 1 — the row records who created
 * it and when — so `0` would be a count for a file that has none. The scale
 * has no off position, deliberately.
 */
export const SEEDED_DIR_RETENTION = 1

/**
 * Directory names seeded at {@link SEEDED_DIR_RETENTION}.
 *
 * Matched against a single path SEGMENT — never a substring — so
 * `my-node_modules/` and `node_modules.bak/` are ordinary user folders and get
 * the tenant's normal retention. Read the admission rule above before adding
 * to this set.
 */
export const SEEDED_DIR_NAMES: readonly string[] = [
  // Git's private bookkeeping. The file-count cost of a fresh clone is small
  // (packfiles, not loose objects); the CHURN cost is the reason it is here —
  // `git status` alone rewrites the multi-megabyte `.git/index`, and every
  // bash call that touches git was filing a new binary version of it.
  '.git',
  // The file-count problem. ~110,000 files on this repository alone,
  // reproducible from `package.json` + a lockfile.
  'node_modules',
  // Python's compiled-bytecode cache. Regenerated from the `.py` beside it,
  // and rewritten on every interpreter run — the same churn shape as
  // `.git/index`, at a smaller size.
  '__pycache__',
]

/**
 * The seed, in RULE form.
 *
 * This is what an unconfigured tenant resolves to, and what the settings tab
 * renders as its starting rows so the user can edit, rename or delete them
 * like any other rule.
 */
export const BUILTIN_RULES: readonly ExclusionRule[] =
  [...SEEDED_DIR_NAMES].sort().map(pattern => ({ pattern, versions: SEEDED_DIR_RETENTION }))

/** Is `pattern` (normalised) one of the built-in rules (`.git`, `node_modules`, `__pycache__`)? The retention preview
 *  marks them (#1442), so a `.pyc` going 6 → 1 reads as the built-in rule, not the change being saved. */
export function isBuiltInFolderPattern(pattern: string): boolean {
  return BUILTIN_RULES.some((r) => r.pattern === pattern)
}

/**
 * The rules in force for a tenant: their own if they have any, else the seed.
 *
 * Pure, and the ONE place the two lists meet — the resolver, the preview and
 * the settings route all go through it, so none of them can form a different
 * opinion about what is in force.
 *
 * **This does not MERGE.** A tenant with a stored list gets exactly that list,
 * including one that deletes `.git`. Layering the defaults underneath would
 * make them undeletable, which is the thing being fixed: a row a user can see
 * and edit but not remove is a worse surface than no row at all.
 *
 * Patterns are normalised on the way through, so `.git/` and `.git` are one
 * rule rather than two rows that disagree — the same reason the write path
 * stores normalised patterns. Otherwise invalid entries are left for
 * `compileExclusions` to drop: this function selects, it does not validate,
 * and a stored rule that has since become invalid must degrade to "not in
 * force" rather than take the sync down.
 *
 * ── ONE SHAPE IS REPAIRED RATHER THAN DROPPED ─────────────────────────────
 *
 * A rule stored while "not synced" existed carries `versions: null`. Dropping
 * it would silently promote that folder to the tenant's DEFAULT retention —
 * 50 versions of `node_modules` — which is the loudest possible wrong answer
 * and would arrive with no audit row and nothing on screen to explain it. It
 * is read as {@link SEEDED_DIR_RETENTION} instead, the value the rule would
 * have been seeded with today, and the next save rewrites it in the new shape.
 */
export function seedFolderRules(
  tenantRules: readonly (ExclusionRule | { pattern: string; versions: unknown })[] | null | undefined,
): ExclusionRule[] {
  if (!tenantRules || tenantRules.length === 0) return [...BUILTIN_RULES]
  const out: ExclusionRule[] = []
  for (const r of tenantRules) {
    if (!r || typeof r.pattern !== 'string') continue
    const n = normalizePattern(r.pattern)
    if (!n.ok) continue
    const versions = r.versions === null
      ? SEEDED_DIR_RETENTION
      : r.versions
    if (!isValidFolderRetention(versions)) continue
    out.push({ pattern: n.value, versions })
  }
  return out
}
